import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatFileSize } from "@/hooks/use-project-documents";

export interface AttachmentItem {
  id: string;
  name: string;
  /** Bytes, hvis kjent. */
  size: number | null;
  /** Sekundærtekst (f.eks. dato/forfatter eller mersalg-tittel). */
  meta?: string | null;
  /** Åpner vedlegget i ny fane (dekker visning + nedlasting). */
  onOpen: () => void;
}

/**
 * VIB-996: read-only seksjon på Dokumenter-siden som samler vedlegg fra en
 * ekstern kilde (prosjektoppdateringer / mersalg). Sletting skjer i kilden,
 * her er det kun visning + nedlasting. Rendrer ingenting når listen er tom.
 */
export function ProjectAttachmentSection({
  title,
  items,
}: {
  title: string;
  items: AttachmentItem[];
}) {
  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {items.map((item) => {
        const ext = item.name.split(".").pop()?.toUpperCase().slice(0, 3) || "FIL";
        return (
          <div
            key={item.id}
            className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/20 transition-colors"
          >
            <div className="w-8 h-8 bg-muted rounded flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-muted-foreground">{ext}</span>
            </div>
            <div className="flex-1 min-w-0">
              <button
                onClick={item.onOpen}
                className="text-sm font-medium hover:underline truncate block text-left"
              >
                {item.name}
              </button>
              <div className="text-xs text-muted-foreground space-x-2">
                {item.size != null && <span>{formatFileSize(item.size)}</span>}
                {item.size != null && item.meta && <span>•</span>}
                {item.meta && <span>{item.meta}</span>}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={item.onOpen}
              className="h-8 w-8 p-0"
              title="Åpne i ny fane"
              aria-label={`Åpne ${item.name}`}
            >
              <SrIcon name="external-link" className="w-4 h-4 text-muted-foreground" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
